import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, AlertCircle, FileText, Send, X, ExternalLink, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ComplianceAlerts = ({ compact = false }) => {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchAlerts();
    // Refresh every 5 minutes
    const interval = setInterval(fetchAlerts, 300000);
    return () => clearInterval(interval);
  }, []);

  const fetchAlerts = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/compliance/alerts`, { withCredentials: true });
      setAlerts(response.data);
    } catch (error) {
      console.error('Error fetching compliance alerts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSendNotifications = async () => {
    setSending(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/compliance/alerts/send-notifications`, {}, { withCredentials: true });
      toast.success(`${response.data.notifications_sent} notificações enviadas por email!`);
    } catch (error) {
      toast.error('Erro ao enviar notificações');
    } finally {
      setSending(false);
    }
  };

  const handleDismissAlert = async (alertId) => {
    try {
      await axios.post(`${BACKEND_URL}/api/compliance/alerts/${alertId}/dismiss`, {}, { withCredentials: true });
      setAlerts(alerts.filter(a => a.alert_id !== alertId));
      toast.success('Alerta dispensado');
    } catch (error) {
      toast.error('Erro ao dispensar alerta');
    }
  };

  const getSeverityStyles = (severity) => {
    switch (severity) {
      case 'critical':
        return {
          bg: 'bg-red-50 border-red-200',
          badge: 'bg-red-100 text-red-700',
          icon: <AlertCircle className="w-5 h-5 text-red-600" />
        };
      case 'warning':
        return {
          bg: 'bg-amber-50 border-amber-200',
          badge: 'bg-amber-100 text-amber-700',
          icon: <AlertTriangle className="w-5 h-5 text-amber-600" />
        };
      default:
        return {
          bg: 'bg-blue-50 border-blue-200',
          badge: 'bg-blue-100 text-blue-700',
          icon: <FileText className="w-5 h-5 text-blue-600" />
        };
    }
  };

  if (loading) {
    return (
      <Card data-testid="compliance-alerts-widget">
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </CardContent>
      </Card>
    );
  }

  if (alerts.length === 0) {
    return compact ? null : (
      <Card data-testid="compliance-alerts-widget">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-slate-400" />
            Alertas de Conformidade
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-4 text-slate-500">
            <AlertTriangle className="w-8 h-8 mx-auto mb-2 text-emerald-500" />
            <p className="text-sm">Sem alertas de conformidade pendentes</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const criticalCount = alerts.filter(a => a.severity === 'critical').length;
  const warningCount = alerts.filter(a => a.severity === 'warning').length;

  return (
    <Card data-testid="compliance-alerts-widget" className={criticalCount > 0 ? 'border-red-200' : warningCount > 0 ? 'border-amber-200' : ''}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <AlertTriangle className={`w-5 h-5 ${criticalCount > 0 ? 'text-red-500' : 'text-amber-500'}`} />
            Alertas de Conformidade
            <Badge variant="outline" className={criticalCount > 0 ? 'bg-red-50 text-red-700' : 'bg-amber-50 text-amber-700'}>
              {alerts.length}
            </Badge>
          </CardTitle>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleSendNotifications}
              disabled={sending}
              className="text-xs"
            >
              {sending ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Send className="w-3 h-3 mr-1" />}
              Enviar Alertas
            </Button>
          </div>
        </div>
        {(criticalCount > 0 || warningCount > 0) && (
          <div className="flex gap-2 mt-2">
            {criticalCount > 0 && (
              <Badge className="bg-red-100 text-red-700">
                {criticalCount} Crítico{criticalCount > 1 ? 's' : ''}
              </Badge>
            )}
            {warningCount > 0 && (
              <Badge className="bg-amber-100 text-amber-700">
                {warningCount} Aviso{warningCount > 1 ? 's' : ''}
              </Badge>
            )}
          </div>
        )}
      </CardHeader>
      <CardContent className="space-y-3">
        {(compact ? alerts.slice(0, 3) : alerts).map((alert) => {
          const styles = getSeverityStyles(alert.severity);
          return (
            <div 
              key={alert.alert_id} 
              className={`p-3 rounded-lg border ${styles.bg} transition-all hover:shadow-sm`}
            >
              <div className="flex items-start gap-3">
                {styles.icon}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="font-medium text-[#0F172A]">{alert.title}</span>
                    <Badge className={`text-xs ${styles.badge}`}>
                      {alert.type === 'dpa_pending' ? 'DPA' : 'Contrato'}
                    </Badge>
                    {alert.days_pending && (
                      <span className="text-xs text-slate-500">{alert.days_pending} dias</span>
                    )}
                    {alert.days_since_creation && (
                      <span className="text-xs text-slate-500">{alert.days_since_creation} dias</span>
                    )}
                  </div>
                  <p className="text-sm text-slate-600 line-clamp-2">{alert.message}</p>
                  <div className="mt-2 flex items-center gap-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="h-7 text-xs"
                      onClick={() => navigate('/compliance')}
                    >
                      <ExternalLink className="w-3 h-3 mr-1" />
                      Ver Detalhes
                    </Button>
                  </div>
                </div>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="h-6 w-6 flex-shrink-0"
                  onClick={() => handleDismissAlert(alert.alert_id)}
                  title="Dispensar"
                >
                  <X className="w-4 h-4 text-slate-400" />
                </Button>
              </div>
            </div>
          );
        })}
        
        {compact && alerts.length > 3 && (
          <Button 
            variant="link" 
            className="w-full text-sm"
            onClick={() => navigate('/compliance')}
          >
            Ver todos os {alerts.length} alertas
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

export default ComplianceAlerts;
